import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
  confirmPasswordReset,
  updatePassword,
  updateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  createUserWithEmailAndPassword,
  updateProfile,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection, getDocs, deleteDoc, query, where } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { auth, db } from '../lib/firebase';
import { AppRole, AppPermission } from '../types';
import { handleFirestoreError, OperationType } from '../lib/firestoreErrors';

export interface FirebaseUserProfile {
  uid: string;
  id?: number;
  email: string;
  displayName: string;
  name?: string;
  login?: string;
  role: AppRole;
  active: boolean;
  unit?: string;
  // null = segue as permissões do grupo (calculadas ao vivo); array = lista
  // customizada fixa para este usuário, que ignora mudanças futuras no grupo.
  custom_permissions?: AppPermission[] | null;
  groupIds?: number[];
  createdAt?: string;
  updatedAt?: string;
}

export class FirebaseAuthService {
  /**
   * Realiza login com E-mail e Senha no Firebase Auth
   */
  static async loginWithEmailPassword(email: string, password: string): Promise<{ user: FirebaseUser }> {
    const userCredential = await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
    return { user: userCredential.user };
  }

  /**
   * Realiza login com Google no Firebase Auth.
   * No app Android/iOS usa o fluxo nativo (evita o popup do Firebase, que não
   * funciona dentro do WebView do Capacitor porque a aba aberta pelo Google
   * não compartilha o sessionStorage com o app). No navegador usa o popup normal.
   */
  static async loginWithGoogle(): Promise<{ user: FirebaseUser }> {
    if (Capacitor.isNativePlatform()) {
      const result = await FirebaseAuthentication.signInWithGoogle();
      if (!result.user || !auth.currentUser) {
        throw new Error('Login com Google cancelado ou não sincronizado.');
      }
      return { user: auth.currentUser };
    }

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const userCredential = await signInWithPopup(auth, provider);
    return { user: userCredential.user };
  }

  /**
   * Cria novo usuário no Firebase Auth
   */
  static async registerUser(email: string, password: string, displayName?: string, role: AppRole = 'user'): Promise<{ user: FirebaseUser }> {
    const userCredential = await createUserWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
    const user = userCredential.user;

    if (displayName) {
      await updateProfile(user, { displayName });
    }

    // Salva perfil inicial no Firestore
    try {
      await setDoc(doc(db, 'users', user.uid), {
        userId: user.uid,
        email: user.email?.toLowerCase() || '',
        displayName: displayName || user.displayName || '',
        role: role,
        active: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }, { merge: true });
    } catch (e) {
      console.warn('Perfil Firestore inicial:', e);
    }

    return { user };
  }

  /**
   * Desconecta o usuário do Firebase
   */
  static async logout(): Promise<void> {
    localStorage.removeItem('userUnit');
    await signOut(auth);
  }

  /**
   * Envia e-mail de recuperação de senha pelo Firebase Auth
   */
  static async sendPasswordReset(email: string): Promise<void> {
    const cleanEmail = email.trim().toLowerCase();
    try {
      const actionCodeSettings = {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false
      };
      await sendPasswordResetEmail(auth, cleanEmail, actionCodeSettings);
    } catch (err: any) {
      // Se falhar devido a domínio não autorizado no actionCodeSettings, tenta envio padrão do Firebase
      if (err?.code === 'auth/unauthorized-continue-uri' || err?.code === 'auth/invalid-continue-uri') {
        await sendPasswordResetEmail(auth, cleanEmail);
      } else {
        throw err;
      }
    }
  }

  /**
   * Confirma redefinição de senha com código oobCode
   */
  static async confirmResetPassword(oobCode: string, newPassword: string): Promise<void> {
    await confirmPasswordReset(auth, oobCode, newPassword);
  }

  /**
   * Atualiza a senha do usuário atualmente autenticado
   */
  static async updateCurrentUserPassword(newPassword: string): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('Nenhum usuário autenticado para atualizar a senha.');
    }
    await updatePassword(auth.currentUser, newPassword);
  }

  /**
   * Verifica se um usuário foi excluído do sistema (UID, e-mail ou ID numérico na coleção deleted_users)
   */
  static async isUserDeleted(uid?: string, email?: string, id?: number | string): Promise<boolean> {
    try {
      const deletedIds = await FirebaseAuthService.getDeletedUserIdsFromFirestore();
      const normEmail = email ? email.trim().toLowerCase() : '';
      const strId = id !== undefined ? String(id).trim() : '';
      const strUid = uid ? uid.trim() : '';

      return (
        (strUid && deletedIds.includes(strUid)) ||
        (normEmail && deletedIds.includes(normEmail)) ||
        (strId && deletedIds.includes(strId)) ||
        (strId && deletedIds.includes(Number(strId)))
      );
    } catch (e) {
      console.warn('Erro ao verificar se usuário está excluído:', e);
      return false;
    }
  }

  /**
   * Obtém o perfil de usuário armazenado no Firestore, com busca de fallback por e-mail e auto-migração para UID caso o documento esteja indexed por e-mail.
   */
  static async getUserProfile(uid: string, userEmail?: string): Promise<FirebaseUserProfile | null> {
    const path = `users/${uid}`;
    try {
      const emailToSearch = (userEmail || auth.currentUser?.email || '').trim().toLowerCase();
      
      // Se o usuário estiver marcado como excluído, recusa o perfil imediatamente
      const isDeleted = await FirebaseAuthService.isUserDeleted(uid, emailToSearch);
      if (isDeleted) {
        return null;
      }

      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          uid,
          id: data.id ? Number(data.id) : undefined,
          email: data.email || '',
          displayName: data.displayName || data.name || '',
          name: data.name || data.displayName || '',
          login: data.login || '',
          role: data.role || 'user',
          active: data.active !== false && data.active !== 'N' && data.active !== 'inativo',
          unit: data.unit || 'matriz',
          custom_permissions: Array.isArray(data.custom_permissions) ? data.custom_permissions : undefined,
          groupIds: Array.isArray(data.groupIds) ? data.groupIds : undefined,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt
        };
      }

      // Fallback: Se não achou pelo ID do documento (UID), busca por campo 'email'
      if (emailToSearch) {
        const q = query(collection(db, 'users'), where('email', '==', emailToSearch));
        const querySnap = await getDocs(q);
        if (!querySnap.empty) {
          const matchedDoc = querySnap.docs[0];
          const data = matchedDoc.data();
          
          // Cria o novo perfil associado ao UID correto
          const profile: FirebaseUserProfile = {
            uid,
            id: data.id ? Number(data.id) : undefined,
            email: data.email || emailToSearch,
            displayName: data.displayName || data.name || '',
            name: data.name || data.displayName || '',
            login: data.login || '',
            role: data.role || 'user',
            active: data.active !== false && data.active !== 'N' && data.active !== 'inativo',
            unit: data.unit || 'matriz',
            custom_permissions: Array.isArray(data.custom_permissions) ? data.custom_permissions : undefined,
            groupIds: Array.isArray(data.groupIds) ? data.groupIds : undefined,
            createdAt: data.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };

          // Salva novo sob o UID
          await setDoc(doc(db, 'users', uid), {
            userId: uid,
            ...profile
          });

          // Deleta documento legado antigo se o ID for diferente do UID
          if (matchedDoc.id !== uid) {
            await deleteDoc(doc(db, 'users', matchedDoc.id)).catch(() => {});
          }

          return profile;
        }
      }
      return null;
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, path, auth.currentUser);
      return null;
    }
  }

  /**
   * Obtém todos os perfis armazenados no Firestore para listagem completa.
   * Auto-semeia os usuários reais do Firebase Auth se a coleção do Firestore estiver incompleta,
   * MAS NUNCA recria usuários que foram marcados como excluídos.
   */
  static async getAllFirestoreUsers(): Promise<FirebaseUserProfile[]> {
    try {
      const [snap, deletedList] = await Promise.all([
        getDocs(collection(db, 'users')),
        FirebaseAuthService.getDeletedUserIdsFromFirestore()
      ]);

      const deletedSet = new Set(deletedList.map(item => String(item).trim().toLowerCase()));

      const list: FirebaseUserProfile[] = [];
      const emailsInFirestore = new Set<string>();

      snap.forEach(docSnap => {
        const data = docSnap.data();
        const email = (data.email || '').trim().toLowerCase();
        const docId = docSnap.id.trim().toLowerCase();
        const numId = data.id !== undefined ? String(data.id).trim().toLowerCase() : '';

        // Ignora se estiver na lista de excluídos
        if (
          deletedSet.has(docId) ||
          (email && deletedSet.has(email)) ||
          (numId && deletedSet.has(numId))
        ) {
          return;
        }

        if (email) emailsInFirestore.add(email);
        list.push({
          uid: docSnap.id,
          id: data.id ? Number(data.id) : undefined,
          email: email,
          displayName: data.displayName || data.name || '',
          name: data.name || data.displayName || '',
          login: data.login || '',
          role: data.role || 'user',
          active: data.active !== false && data.active !== 'N' && data.active !== 'inativo',
          unit: data.unit || 'matriz',
          custom_permissions: Array.isArray(data.custom_permissions) ? data.custom_permissions : undefined,
          groupIds: Array.isArray(data.groupIds) ? data.groupIds : undefined,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt
        });
      });

      // Lista base de usuários de apoio inicial
      const REAL_USERS_TO_SEED = [
        {
          email: 'hugo.abdalaengenharia@gmail.com',
          name: 'Hugo Abdala',
          displayName: 'Hugo Abdala',
          role: 'admin' as AppRole,
          groupIds: [1],
          active: true,
          id: 9001
        },
        {
          email: 'comercial@voopetrans.com.br',
          name: 'Equipe Comercial',
          displayName: 'Equipe Comercial',
          role: 'user' as AppRole,
          groupIds: [2, 4],
          active: true,
          id: 9012,
          custom_permissions: [
            'view_dashboard', 'view_orders', 'manage_orders', 'create_orders',
            'view_proposals', 'manage_proposals', 'view_people', 'create_people',
            'manage_people', 'view_medicoes'
          ] as AppPermission[]
        },
        {
          email: 'financeiro@voopetrans.com.br',
          name: 'Equipe Financeiro',
          displayName: 'Equipe Financeiro',
          role: 'user' as AppRole,
          groupIds: [3],
          active: true,
          id: 9011,
          custom_permissions: [
            'view_dashboard', 'view_financial', 'create_financial', 'manage_financial',
            'pay_financial', 'view_financial_dre', 'view_medicoes', 'create_medicoes',
            'manage_medicoes', 'view_orders', 'view_order_prices'
          ] as AppPermission[]
        },
        {
          email: 'pb.neliosantos@gmail.com',
          name: 'Nélio Santos',
          displayName: 'Nélio Santos',
          role: 'user' as AppRole,
          groupIds: [2],
          active: true,
          id: 9101
        },
        {
          email: 'rafael@voopetrans.com.br',
          name: 'Rafael',
          displayName: 'Rafael',
          role: 'user' as AppRole,
          groupIds: [2],
          active: true,
          id: 9102
        },
        {
          email: 'contato@voopetrans.com.br',
          name: 'Atendimento Voope',
          displayName: 'Atendimento Voope',
          role: 'user' as AppRole,
          groupIds: [4],
          active: true,
          id: 9015
        }
      ];

      // Filtra os usuários que faltam e adiciona em memória, MAS NÃO se tiverem sido excluídos
      for (const seededUser of REAL_USERS_TO_SEED) {
        const normEmail = seededUser.email.trim().toLowerCase();
        const strId = String(seededUser.id);
        if (deletedSet.has(normEmail) || deletedSet.has(strId)) {
          continue; // Usuário foi expressamente excluído pelo administrador
        }

        if (!emailsInFirestore.has(normEmail)) {
          const docId = normEmail;
          list.push({
            uid: docId,
            id: seededUser.id,
            email: normEmail,
            displayName: seededUser.displayName,
            name: seededUser.name,
            login: normEmail.split('@')[0],
            role: seededUser.role,
            active: seededUser.active,
            unit: 'matriz',
            groupIds: seededUser.groupIds,
            custom_permissions: seededUser.custom_permissions || [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
        }
      }

      return list;
    } catch (e) {
      console.warn('Erro ao buscar usuários do Firestore:', e);
      return [];
    }
  }

  /**
   * Atualiza ou sincroniza o perfil no Firestore
   */
  static async saveUserProfile(uid: string, profileData: Partial<FirebaseUserProfile>): Promise<void> {
    if (!uid || typeof uid !== 'string') {
      console.warn('saveUserProfile: UID inválido fornecido');
      return;
    }
    const path = `users/${uid}`;
    try {
      await setDoc(doc(db, 'users', uid), {
        userId: uid,
        ...profileData,
        updatedAt: new Date().toISOString()
      }, { merge: true });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, path, auth.currentUser);
    }
  }

  /**
   * Exclui o perfil do usuário no Firestore (remove por UID, por e-mail e por ID numérico de forma atômica/paralela aguardada)
   */
  static async deleteUserProfile(uid: string, email?: string, userId?: number | string): Promise<void> {
    try {
      const deletePromises: Promise<void>[] = [];

      if (uid && uid.trim()) {
        deletePromises.push(deleteDoc(doc(db, 'users', uid.trim())).catch(() => {}));
      }

      if (email && email.trim()) {
        const cleanEmail = email.trim().toLowerCase();
        deletePromises.push(deleteDoc(doc(db, 'users', cleanEmail)).catch(() => {}));
        
        try {
          const q = query(collection(db, 'users'), where('email', '==', cleanEmail));
          const querySnap = await getDocs(q);
          if (!querySnap.empty) {
            querySnap.docs.forEach((d) => {
              deletePromises.push(deleteDoc(doc(db, 'users', d.id)).catch(() => {}));
            });
          }
        } catch (queryErr) {
          console.warn('Aviso ao consultar usuários por e-mail para exclusão:', queryErr);
        }
      }

      if (userId !== undefined && userId !== null && String(userId).trim() !== '') {
        const strId = String(userId).trim();
        deletePromises.push(deleteDoc(doc(db, 'users', `user_${strId}`)).catch(() => {}));
        deletePromises.push(deleteDoc(doc(db, 'users', strId)).catch(() => {}));
      }

      await Promise.all(deletePromises);
    } catch (e) {
      console.warn('Erro ao deletar perfil no Firestore:', e);
    }
  }

  /**
   * Busca lista de IDs de usuários excluídos gravada no Firestore
   */
  static async getDeletedUserIdsFromFirestore(): Promise<(number | string)[]> {
    try {
      const snap = await getDocs(collection(db, 'deleted_users'));
      const list: (number | string)[] = [];
      snap.forEach(docSnap => {
        const data = docSnap.data();
        if (data.id !== undefined) list.push(data.id);
        if (data.email) list.push(data.email.trim().toLowerCase());
        if (data.uid) list.push(data.uid.trim());
        list.push(docSnap.id);
      });
      return list;
    } catch (e) {
      console.warn('Erro ao buscar usuários excluídos do Firestore:', e);
      return [];
    }
  }

  static async getDeletedUsersPayloadFromFirestore(): Promise<any[]> {
    try {
      const snap = await getDocs(collection(db, 'deleted_users'));
      const list: any[] = [];
      snap.forEach(docSnap => {
        list.push(docSnap.data());
      });
      return list;
    } catch (e) {
      console.warn('Erro ao buscar payloads de usuários excluídos:', e);
      return [];
    }
  }

  /**
   * Marca um usuário como excluído no Firestore (persistente entre sessões)
   */
  static async markUserDeletedInFirestore(userId: number | string, email?: string, uid?: string): Promise<void> {
    try {
      const strId = String(userId).trim();
      const normEmail = email ? email.trim().toLowerCase() : '';
      const strUid = uid ? uid.trim() : '';

      const payload = {
        id: userId,
        email: normEmail,
        uid: strUid,
        deletedAt: new Date().toISOString()
      };

      if (strId) {
        await setDoc(doc(db, 'deleted_users', strId), payload);
      }
      if (normEmail && normEmail !== strId) {
        await setDoc(doc(db, 'deleted_users', normEmail), payload);
      }
      if (strUid && strUid !== strId && strUid !== normEmail) {
        await setDoc(doc(db, 'deleted_users', strUid), payload);
      }
    } catch (e) {
      console.warn('Erro ao marcar usuário como excluído no Firestore:', e);
    }
  }

  /**
   * Remove a marca de excluído no Firestore (limpa por ID, e-mail e ID do documento)
   */
  static async unmarkUserDeletedInFirestore(identifier: number | string): Promise<void> {
    try {
      const strId = String(identifier).trim().toLowerCase();
      if (!strId) return;

      // Deleta por chave direta de ID
      await deleteDoc(doc(db, 'deleted_users', strId)).catch(() => {});

      // Busca e remove quaisquer outros documentos com ID ou e-mail correspondente
      const snap = await getDocs(collection(db, 'deleted_users'));
      for (const docSnap of snap.docs) {
        const data = docSnap.data();
        if (
          docSnap.id.toLowerCase() === strId ||
          String(data.id).toLowerCase() === strId ||
          (data.email && data.email.trim().toLowerCase() === strId) ||
          (data.uid && data.uid.trim().toLowerCase() === strId)
        ) {
          await deleteDoc(doc(db, 'deleted_users', docSnap.id)).catch(() => {});
        }
      }
    } catch (e) {
      console.warn('Erro ao desmarcar usuário excluído:', e);
    }
  }

  /**
   * Limpa todos os bloqueios de usuários excluídos e restaura a visibilidade total
   */
  static async clearAllDeletedUsers(): Promise<number> {
    try {
      const snap = await getDocs(collection(db, 'deleted_users'));
      let count = 0;
      for (const docSnap of snap.docs) {
        await deleteDoc(doc(db, 'deleted_users', docSnap.id)).catch(() => {});
        count++;
      }
      return count;
    } catch (e) {
      console.warn('Erro ao limpar deleted_users:', e);
      return 0;
    }
  }

  /**
   * Sincroniza e restaura todos os usuários do Firebase Auth / Firestore,
   * limpando bloqueios de exclusão e garantindo que todos os perfis fiquem ativos e visíveis.
   */
  static async syncAndRestoreAllFirebaseUsers(): Promise<FirebaseUserProfile[]> {
    try {
      // 1. Limpa marcas de exclusão
      await FirebaseAuthService.clearAllDeletedUsers();

      // 2. Garante que os usuários base existam salvos no Firestore
      const REAL_USERS = [
        {
          email: 'hugo.abdalaengenharia@gmail.com',
          displayName: 'Hugo Abdala',
          role: 'admin' as const,
          id: 1,
          login: 'hugo.abdala'
        },
        {
          email: 'contato@voopetrans.com.br',
          displayName: 'Voope Transportes',
          role: 'admin' as const,
          id: 2,
          login: 'voope.admin'
        },
        {
          email: 'pedro.afreitas93@gmail.com',
          displayName: 'Pedro Freitas',
          role: 'admin' as const,
          id: 3,
          login: 'pedro.freitas'
        },
        {
          email: 'diego.sampaio@voopetrans.com.br',
          displayName: 'Diego Sampaio',
          role: 'admin' as const,
          id: 4,
          login: 'diego.sampaio'
        },
        {
          email: 'operacional@voopetrans.com.br',
          displayName: 'Operacional Voope',
          role: 'manager' as const,
          id: 5,
          login: 'operacional'
        },
        {
          email: 'financeiro@voopetrans.com.br',
          displayName: 'Financeiro Voope',
          role: 'manager' as const,
          id: 6,
          login: 'financeiro'
        },
        {
          email: 'faturamento@voopetrans.com.br',
          displayName: 'Faturamento Voope',
          role: 'manager' as const,
          id: 7,
          login: 'faturamento'
        },
        {
          email: 'comercial@voopetrans.com.br',
          displayName: 'Comercial Voope',
          role: 'manager' as const,
          id: 8,
          login: 'comercial'
        },
        {
          email: 'mariana.costa@voopetrans.com.br',
          displayName: 'Mariana Costa',
          role: 'user' as const,
          id: 9,
          login: 'mariana.costa'
        },
        {
          email: 'carlos.eduardo@voopetrans.com.br',
          displayName: 'Carlos Eduardo',
          role: 'user' as const,
          id: 10,
          login: 'carlos.eduardo'
        },
        {
          email: 'roberto.almeida@voopetrans.com.br',
          displayName: 'Roberto Almeida',
          role: 'user' as const,
          id: 11,
          login: 'roberto.almeida'
        },
        {
          email: 'fernanda.lima@voopetrans.com.br',
          displayName: 'Fernanda Lima',
          role: 'user' as const,
          id: 12,
          login: 'fernanda.lima'
        },
        {
          email: 'marcelo.souza@voopetrans.com.br',
          displayName: 'Marcelo Souza',
          role: 'user' as const,
          id: 13,
          login: 'marcelo.souza'
        },
        {
          email: 'beatriz.santos@voopetrans.com.br',
          displayName: 'Beatriz Santos',
          role: 'user' as const,
          id: 14,
          login: 'beatriz.santos'
        },
        {
          email: 'lucas.oliveira@voopetrans.com.br',
          displayName: 'Lucas Oliveira',
          role: 'user' as const,
          id: 15,
          login: 'lucas.oliveira'
        },
        {
          email: 'juliana.martins@voopetrans.com.br',
          displayName: 'Juliana Martins',
          role: 'user' as const,
          id: 16,
          login: 'juliana.martins'
        },
        {
          email: 'admin@voopetrans.com.br',
          displayName: 'Admin Voope',
          role: 'admin' as const,
          id: 17,
          login: 'admin'
        }
      ];

      for (const u of REAL_USERS) {
        await FirebaseAuthService.saveUserProfile(u.email, {
          uid: u.email,
          id: u.id,
          email: u.email,
          displayName: u.displayName,
          name: u.displayName,
          login: u.login,
          role: u.role,
          active: true,
          unit: 'matriz'
        });
      }

      // 3. Retorna lista atualizada
      return await FirebaseAuthService.getAllFirestoreUsers();
    } catch (e) {
      console.warn('Erro ao sincronizar e restaurar usuários:', e);
      return await FirebaseAuthService.getAllFirestoreUsers();
    }
  }

  /**
   * Monitora alterações de autenticação no Firebase
   */
  static onAuthStateChange(callback: (user: FirebaseUser | null) => void) {
    return onAuthStateChanged(auth, callback);
  }

  /**
   * Obtém usuário atual
   */
  static getCurrentUser(): FirebaseUser | null {
    return auth.currentUser;
  }
}
